"use client";

import React, { useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FaPlay } from "react-icons/fa";
import { Macro } from "@/types/lighthouse";
import { runMacro } from "@/utils/runMacro";

interface MacroCardProps {
  macro: Macro;
  isSelected?: boolean;
  onSelect?: (macro: Macro) => void;
}

export const MacroCard = ({ macro, isSelected = false, onSelect }: MacroCardProps) => {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const handleToggleDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDetailsExpanded(!isDetailsExpanded);
  };

  const handleRunMacro = (e: React.MouseEvent) => {
    e.stopPropagation();
    runMacro(macro.id);
  };

  const handleCardClick = () => {
    if (onSelect) {
      onSelect(macro);
    }
  };

  return (
    <div
      key={macro.id}
      className={`relative p-3 cursor-pointer ${isSelected ? "ring-2 ring-teal-200 rounded-lg" : ""}`}
      onClick={handleCardClick}
    >
      <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-lg border border-white/30 shadow-lg"></div>
      <div className="relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold">{macro.name}</h2>
            <p className="mt-1 text-xs text-white/80">{macro.description}</p>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          {isSelected ? (
            <button
              type="button"
              onClick={handleToggleDetails}
              className="text-xs font-medium flex items-center gap-1 hover:bg-white/10 rounded-md px-1 py-0.5"
            >
              {isDetailsExpanded ? (
                <>
                  Hide details <FiChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Show details <FiChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          ) : (
            <div></div>
          )}

          <button
            onClick={handleRunMacro}
            className={`inline-flex items-center gap-1 rounded-lg px-4 py-1 text-xs font-semibold text-[#1DC3D2] bg-white border-white/20 border-2 shadow-lg hover:scale-105 hover:shadow-xl transform transition-all duration-200`}
          >
            <FaPlay className="mr-2 size-2.5 fill-current" />
            Run
          </button>
        </div>

        {isSelected && isDetailsExpanded && (
          <div className="mt-3 text-white/90 space-y-2 border-l-4 border-teal-300 pl-2">
            <p className="text-xs">{macro.fullDescription}</p>
          </div>
        )}
      </div>
    </div>
  );
};
